import React, { useContext, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { Utils } from '../../Services/Utils';
import { SiteContext, ToastContext } from '../../Contexts';
import { ImageService } from '../../Services/ImageService';
import { ToastFailureMessage } from '../../Models/Models';
import { EntityColors } from '../../Themes/EntityColors';
import FormShell from '../Form/FormShell';
import SaveButton from '../Buttons/SaveButton';
import DiscardButton from '../Buttons/DiscardButton';
import DeleteButton from '../Buttons/DeleteButton';
import CustomTooltip from '../Tooltips/CustomTooltip';

const MAX_UPLOAD_MB = 25;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

function stripExtension(fileName: string): string {
    return fileName.replace(/\.[^./\\]+$/, '');
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ImageUploadComponent = ({ isPopUp = false, onClose }: { isPopUp?: boolean, onClose?: any }) => {
    useEffect(() => {
        Utils.setDocumentTitle('Nahratie obrázkov');
    }, []);

    const { action, icon } = EntityColors.image;
    const config = require('../../config.json');
    const { uploadImages } = ImageService();
    const { currentSite }: any = useContext(SiteContext);
    const { setToast }: any = useContext(ToastContext);

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [metas, setMetas] = useState<Record<string, { id: number; file: File; filePath: string; alt: string }>>({});
    const [showPreview, setShowPreview] = useState(false);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const isDirty = selectedFiles.length > 0 && !uploading;

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const isOverLimit = totalSize > MAX_UPLOAD_BYTES;

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setSelectedFiles(prev => [...prev, ...files]);
        setMetas(prev => {
            const next = { ...prev };
            files.forEach(f => {
                next[f.name] = { id: -1, file: f, filePath: '', alt: prev[f.name]?.alt || stripExtension(f.name) };
            });
            return next;
        });
        e.target.value = '';
    };

    const removeFile = (idx: number) => {
        const removed = selectedFiles[idx];
        setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
        setMetas(prev => {
            const { [removed.name]: _, ...rest } = prev;
            return rest;
        });
    };

    const openModal = (file: File) => {
        setPreviewFile(file);
        setShowPreview(true);
    };
    const changeAlt = (name: string, alt: string) => {
        setMetas(prev => ({ ...prev, [name]: { ...prev[name], alt } }));
    };

    const doSave = async (): Promise<boolean> => {
        if (selectedFiles.length === 0) return false;
        if (isOverLimit) {
            setToast({ message: ToastFailureMessage.filesTooLarge });
            return false;
        }
        setUploading(true);
        const fd = new FormData();
        fd.append('websiteId', currentSite.id.toString());
        Object.values(metas).forEach(meta => {
            fd.append(`images[${meta.file.name}].File`, meta.file);
            fd.append(`images[${meta.file.name}].Alt`, meta.alt);
        });
        await uploadImages(fd, isPopUp, onClose);
        setUploading(false);
        return true;
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSave();
    };

    const formBody = (
        <fieldset
            disabled={uploading}
            className="st-upload-fieldset"
            style={{ '--upload-accent': action, '--upload-border': action + '40', '--upload-tint': action + '08' } as React.CSSProperties}
        >

            {/* Drop zone / file input */}
            <div className="st-upload-dropzone-wrap">
                <label className="st-upload-field-label">
                    Vyberte obrázky
                </label>
                <label className={`st-upload-dropzone${uploading ? ' st-upload-dropzone--disabled' : ''}`}>
                    <FontAwesomeIcon icon={icon} className="st-upload-dropzone-icon" />
                    <span className="st-upload-dropzone-text">
                        Kliknite sem pre výber obrázkov
                    </span>
                    <span className="st-upload-dropzone-ext">
                        {config.allowedExtensions.join(', ')}
                    </span>
                    <input
                        type="file"
                        multiple
                        accept={config.allowedExtensions}
                        onChange={handleFiles}
                        disabled={uploading}
                        className="d-none"
                    />
                </label>
            </div>

            {/* Selected images list */}
            {selectedFiles.length > 0 && (
                <div className="st-upload-list">
                    {selectedFiles.map((file, idx) => (
                        <div key={idx} className="st-upload-row">
                            <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                onClick={() => openModal(file)}
                                className="st-upload-thumb"
                            />
                            <div className="st-upload-row-info">
                                <p className="st-upload-filename">
                                    {Utils.truncateString(file.name, 100)}
                                </p>
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Alternatívny text"
                                    required
                                    value={metas[file.name]?.alt || ''}
                                    onChange={e => changeAlt(file.name, e.target.value)}
                                    disabled={uploading}
                                    className="st-upload-alt-input"
                                />
                            </div>
                            <CustomTooltip message="Odstrániť" tooltipId={`image-remove-${idx}`}>
                                <DeleteButton iconOnly disabled={uploading} onClick={() => removeFile(idx)} />
                            </CustomTooltip>
                        </div>
                    ))}
                </div>
            )}

            {uploading && (
                <p className="st-upload-uploading-msg">
                    Obrázky sa nahrávajú, prosím nezatvárajte stránku...
                </p>
            )}

            {selectedFiles.length > 0 && (
                <p className={`st-upload-size${isOverLimit ? ' st-upload-size--over' : ''}`}>
                    Celková veľkosť: {formatSize(totalSize)} / {MAX_UPLOAD_MB} MB
                    {isOverLimit && '. Prekročený limit, odstráňte niektoré obrázky.'}
                </p>
            )}

            <div className="st-upload-actions">
                {isPopUp
                    ? <DiscardButton onClick={onClose} disabled={uploading} isDirty={isDirty} />
                    : <DiscardButton to="/image/table" disabled={uploading} isDirty={isDirty} />}
                <SaveButton
                    text="Nahrať"
                    disabled={selectedFiles.length === 0 || uploading || isOverLimit}
                    isLoading={uploading}
                    onSave={doSave}
                    websiteId={currentSite.id}
                />
            </div>
        </fieldset>
    );

    return (
        <>
            {isPopUp ? (
                <div className="st-upload-popup">
                    <div className="st-upload-popup-header">
                        <div className="st-upload-popup-header-left">
                            <div
                                className="st-table-shell-icon-badge"
                                style={{ '--shell-accent': action, '--shell-icon-tint': action + '1A' } as React.CSSProperties}
                            >
                                <FontAwesomeIcon icon={icon} className="st-table-shell-icon" />
                            </div>
                            <h2 className="st-table-shell-title">
                                Nahrať obrázky
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            title="Zavrieť"
                            className="st-btn st-btn--outline st-btn--icon st-btn--sm"
                        >
                            <FontAwesomeIcon icon={faXmark} className="st-upload-close-icon" />
                        </button>
                    </div>
                    <form onSubmit={submit}>{formBody}</form>
                </div>
            ) : (
                <FormShell title="Nahrať obrázky" icon={icon} accentColor={action} backHref="/image/table" isDirty={isDirty}>
                    <form onSubmit={submit}>{formBody}</form>
                </FormShell>
            )}

            <Modal show={showPreview} onHide={() => setShowPreview(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{previewFile && Utils.truncateString(previewFile.name, 30)}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    {previewFile && (
                        <img
                            src={URL.createObjectURL(previewFile)}
                            alt={previewFile.name}
                            className="st-upload-preview-img"
                        />
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ImageUploadComponent;
